import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import _ from 'lodash'
import L1Select from '../l1/L1Select'
import { newOncologyReviewItem, OncologyReviewItem } from '@fusion-advantage/formlib'
import { L2TabularListControl, L2TabularListControlLabel, L2TabularListItemEdit, L2TabularListItemSummary } from '../l2/L2TabularListItem'
import { L2TabularListActions, L2TabularListContent, L2TabularListHeader, L2TabularListRoot } from '../l2/L2TabularList'
import { L4NoPrint } from '../l4/L4Print'
import { L1ReasonableButton } from '../l1/L1Button'
import L2ControlGrid from '../l2/L2ControlGrid'
import L2ControlGridLayout from '../l2/L2ControlGridLayout'
import { selectCarcinoidMetastaticSites, useCarcinoidMatch, useMetastaticSiteStatusMatch, usePrimaryTumorStatusMatch } from '../../../redux/selectOncology'
import { useFormSelector } from '../../../redux/formStoreHooks'

type L3OncologyReviewProps = {
	models: OncologyReviewItem[],
	valueLists: OncologyReviewValueLists,
	onChange: (e: OncologyReviewItem[]) => void
}

const L3OncologyReviewContainer = styled.div(({theme}) => ({
	boxSizing: 'border-box',
	padding: 0,
}))

const OncologyReviewRowContainer = styled.div(({theme}) => ({
	padding: '1em',
	width: '100%',
}))

const HeaderSubText = styled.p`
	font-weight: 400;
	margin: 0;
`

export type OncologyReviewValueLists = {
	tumors: string[],
	treatmentStatuses: string[],
	metastaticSites: string[],
	metastaticSiteStatuses: string[],
}

type OncologyReviewRowProps = {
	lists: OncologyReviewValueLists,
	initialValue?: OncologyReviewItem,
	onChange: (model: OncologyReviewItem) => void,
}

const OncologyReviewRow = ({lists, initialValue, onChange}: OncologyReviewRowProps) => {
	const [selectedTumor, setSelectedTumor] = useState(initialValue?.tumor || "")
	const [selectedTreatmentStatus, setSelectedTreatmentStatus] = useState(initialValue?.treatmentStatus || "")
	const [selectedMetastaticSite, setSelectedMetastaticSite] = useState(initialValue?.metastaticSite || "")
	const [selectedMetastaticSiteStatus, setSelectedMetastaticSiteStatus] = useState(initialValue?.metastaticSiteStatus || "")
	const showDebugInfo = useFormSelector(state => state.render.showDebugInfo)
	const carcinoidMatch = useCarcinoidMatch(selectedTumor, selectedMetastaticSite, selectedTreatmentStatus)
	const primaryTumorMatch = usePrimaryTumorStatusMatch(selectedTumor, selectedTreatmentStatus)
	const metastaticMatch = useMetastaticSiteStatusMatch(selectedMetastaticSite, selectedMetastaticSiteStatus)
	const carcinoidSites = useFormSelector(selectCarcinoidMetastaticSites)

	const handleSelectTumor = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedTumor(e.target.value)
	}

	const handleSelectTreatmentStatus = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedTreatmentStatus(e.target.value)
	}

	const handleSelectMetastaticSite = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedMetastaticSite(e.target.value)
	}

	const handleSelectMetastaticSiteStatus = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedMetastaticSiteStatus(e.target.value)
	}

	useEffect(() => {
		handleChange()
	}, [selectedTumor, selectedTreatmentStatus, selectedMetastaticSite, selectedMetastaticSiteStatus])

	// Clear out metastatic site if list doesn't have the currently selected value
	useEffect(() => {
		if (!lists.metastaticSites.includes(selectedMetastaticSite)) {
			setSelectedMetastaticSite("")
		}
	}, [selectedMetastaticSite, lists.metastaticSites])

	const handleChange = useCallback(() => {
		const model: OncologyReviewItem = newOncologyReviewItem(selectedTumor, selectedTreatmentStatus, selectedMetastaticSite, selectedMetastaticSiteStatus)

		onChange(model)
	}, [selectedTumor, selectedTreatmentStatus, selectedMetastaticSite, selectedMetastaticSiteStatus])

	const buildOptionList = useCallback((item: string, index: number, array: string[]) => {
		const nextItem = array[index + 1]
		const needsDivider = item !== "Other" && item !== "Unknown" && 
			(nextItem === "Other" || nextItem === "Unknown")
		return <>
			<option key={item} value={item}>{item}</option>
			{needsDivider && <hr />}
		</>
	}, [])

	return (
		<>
			{
				showDebugInfo ?
				<>
					{
						carcinoidMatch ?
							<div>{carcinoidMatch?.ICD10_1_Eval ?? 'N/A'} & {carcinoidMatch?.ICD10_2_Eval ?? 'N/A'} (Carcinoid Match)</div> :
							<div>{primaryTumorMatch?.ICD10_1_Eval ?? 'N/A'} & {metastaticMatch?.ICD10_1_Eval ?? 'N/A'}</div>
					}
				</> : null
			}
			<OncologyReviewRowContainer>
				<L2ControlGrid columnCount={2} layout={L2ControlGridLayout.EqualDividedColumn}>
					<L2TabularListControl>
						<L2TabularListControlLabel>Primary tumor type</L2TabularListControlLabel>
						<L1Select onChange={handleSelectTumor} value={selectedTumor}>
						{
							lists.tumors.map((tumor, index, array) => (
								buildOptionList(tumor, index, array)
							))
						}
						</L1Select>
					</L2TabularListControl>

					<L2TabularListControl>
						<L2TabularListControlLabel>Treatment status of primary</L2TabularListControlLabel>
						<L1Select onChange={handleSelectTreatmentStatus} value={selectedTreatmentStatus}>
						{
							lists.treatmentStatuses.map((ts, index, array) => (
								buildOptionList(ts, index, array)
							))
						}
						</L1Select>
					</L2TabularListControl>
				</L2ControlGrid>
				<br/>
				<L2ControlGrid columnCount={2} layout={L2ControlGridLayout.EqualDividedColumn}>
					<L2TabularListControl>
						<L2TabularListControlLabel>Metastatic site</L2TabularListControlLabel>
						<L1Select onChange={handleSelectMetastaticSite} value={selectedMetastaticSite}>
						{
							(selectedTumor === 'Carcinoid' ? carcinoidSites : lists.metastaticSites).map((ms, index, array) => (
								buildOptionList(ms, index, array)
							))
						}
						</L1Select>
					</L2TabularListControl>

					<L2TabularListControl>
						<L2TabularListControlLabel>Status of metastasis</L2TabularListControlLabel>
						<L1Select onChange={handleSelectMetastaticSiteStatus} value={selectedMetastaticSiteStatus}>
						{
							lists.metastaticSiteStatuses.map((mss, index, array) => (
								buildOptionList(mss, index, array)
							))
						}
						</L1Select>
					</L2TabularListControl>
				</L2ControlGrid>
			</OncologyReviewRowContainer>
		</>
	)
}

const L3OncologyReview = ({models, valueLists, onChange}: L3OncologyReviewProps) => {
	const [modelState, setModelState] = useState<OncologyReviewItem[]>(models)
	const [editIndex, setEditIndex] = useState<number|null>(null)
	const theme = useTheme()

	useEffect(() => {
		onChange(modelState)
	}, [modelState, editIndex])

	const handleAddModel = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()

		setModelState((prev) => [...prev, newOncologyReviewItem()])
		setEditIndex(modelState.length)
	}

	const handleUpdateModel = (model: OncologyReviewItem, idx: number) => {
		const modelsTemp = _.cloneDeep(modelState)
		modelsTemp.splice(idx, 1, model)

		setModelState(modelsTemp)
	}

	const handleRemoveModel = (idx: number) => {
		if(confirm('Delete this item?')) {
			const modelsTemp = _.cloneDeep(modelState)
			modelsTemp.splice(idx, 1)
	
			if(editIndex === idx) {
				handleToggleEditRow(idx)
			}
	
			setModelState(modelsTemp)
		}
	}

	const handleToggleEditRow = (idx: number) => {
		if(editIndex === idx) {
			return setEditIndex(null)
		}

		setEditIndex(idx)
	}

	return (
		<L3OncologyReviewContainer>
			<L2TabularListRoot columnCount={4}>
				<L2TabularListHeader>
					<div>Primary tumor type</div>
					<div>Treatment status of primary <HeaderSubText>(indicate the current status of each primary site)</HeaderSubText></div>
					<div>Metastatic site <HeaderSubText>(Leave blank for no known metastasis; for multiple metastatic sites use Add function and repeat the primary site)</HeaderSubText></div>
					<div>Status of metastasis <HeaderSubText>(indicate the current status of each metastatic site)</HeaderSubText></div>
				</L2TabularListHeader>
				<L2TabularListContent>
				{
					modelState.map((model, idx) => (
						editIndex === idx ? (
							<L2TabularListItemEdit
								key={idx}
								onCloseClick={() => handleToggleEditRow(idx)}
								onDeleteClick={() => handleRemoveModel(idx)}
							>
								<OncologyReviewRow initialValue={model} lists={valueLists} onChange={(m) => handleUpdateModel(m, idx)}  />
							</L2TabularListItemEdit>
						) : (
							<L2TabularListItemSummary
								key={idx}
								onClick={() => handleToggleEditRow(idx)}
							>
								<div>{model.tumor}</div>
								<div>{model.treatmentStatus}</div>
								<div>{model.metastaticSite}</div>
								<div>{model.metastaticSiteStatus}</div>
							</L2TabularListItemSummary>
						)
						
					))
				}
				</L2TabularListContent>
				<L2TabularListActions>
					<L4NoPrint>
						<L1ReasonableButton icon={faPlus} label="Add" onClick={handleAddModel} />
					</L4NoPrint>
				</L2TabularListActions>
			</L2TabularListRoot>
		</L3OncologyReviewContainer>
	)
}

export default L3OncologyReview